import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Coffee, QrCodeScanner } from '@mui/icons-material';
import { Html5Qrcode } from 'html5-qrcode';
import { useAuth } from '../context/AuthContext';
import { unlockNotificationAudio } from '../services/notifications';

const QR_READER_ID = 'barista-login-qr-reader';

/** Предпочесть заднюю камеру по label (на телефонах); иначе первую доступную. */
function pickCameraDevice(devices) {
  if (!devices?.length) return null;
  const isBack = (label) =>
    /back|rear|environment|wide angle|world|задн/i.test(label || '');
  const found = devices.find((d) => isBack(d.label));
  return found || devices[0];
}

const LoginForm = () => {
  const [deviceToken, setDeviceToken] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [qrDialogError, setQrDialogError] = useState('');
  /** Камера запущена (есть превью). До нажатия «Включить камеру» запрос разрешения не показываем — иначе теряется user gesture. */
  const [qrScannerStarted, setQrScannerStarted] = useState(false);
  const [qrStarting, setQrStarting] = useState(false);
  const fileInputRef = useRef(null);
  const scannerRef = useRef(null);
  const scanHandledRef = useRef(false);
  const loginRef = useRef(null);
  const navigateRef = useRef(null);
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  loginRef.current = login;
  navigateRef.current = navigate;

  const stopScanner = useCallback(async () => {
    const instance = scannerRef.current;
    scannerRef.current = null;
    if (!instance) return;
    try {
      await instance.stop();
    } catch (_) {
      /* уже остановлен */
    }
    try {
      instance.clear();
    } catch (_) {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/orders', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const applyQrTokenAndLogin = useCallback(async (rawText) => {
    const text = rawText.trim();
    await stopScanner();
    setQrOpen(false);
    setQrDialogError('');
    setDeviceToken(text);
    setError('');
    if (!text.includes('.')) {
      setError('QR не содержит токен в формате <device_id>.<secret>');
      return;
    }
    setIsLoading(true);
    try {
      void unlockNotificationAudio();
      await loginRef.current(text);
      navigateRef.current('/orders');
    } catch (err) {
      setError(err.message || 'Ошибка входа. Проверьте токен устройства и попробуйте снова.');
    } finally {
      setIsLoading(false);
    }
  }, [stopScanner]);

  useEffect(() => {
    if (!qrOpen) {
      void stopScanner();
      setQrScannerStarted(false);
      setQrStarting(false);
    }
  }, [qrOpen, stopScanner]);

  const handleStartQrCamera = async () => {
    setQrDialogError('');
    setQrStarting(true);
    scanHandledRef.current = false;

    try {
      await stopScanner();

      if (!navigator.mediaDevices?.getUserMedia) {
        setQrDialogError(
          'Браузер не отдаёт доступ к камере. Откройте приложение по https:// или http://localhost (не по IP в локальной сети без HTTPS).'
        );
        return;
      }

      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        stream.getTracks().forEach((t) => t.stop());
      } catch (e) {
        const name = e?.name || '';
        if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
          setQrDialogError(
            'Доступ к камере запрещён. Нажмите на значок замка или «i» слева от адреса сайта → разрешите камеру для этой страницы и нажмите кнопку снова.'
          );
          return;
        }
        if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
          setQrDialogError(
            'Камера не найдена. Подключите веб-камеру или используйте «Изображение с QR».'
          );
          return;
        }
        if (name === 'NotReadableError' || name === 'TrackStartError') {
          setQrDialogError('Камера занята другим приложением. Закройте его и попробуйте снова.');
          return;
        }
        setQrDialogError(`Не удалось получить доступ к камере: ${e.message || e}`);
        return;
      }

      await new Promise((r) => requestAnimationFrame(() => r()));

      const container = document.getElementById(QR_READER_ID);
      if (!container) {
        return;
      }

      const html5QrCode = new Html5Qrcode(QR_READER_ID);
      scannerRef.current = html5QrCode;

      const config = { fps: 10, qrbox: { width: 280, height: 280 } };

      const onScanSuccess = async (decodedText) => {
        if (scanHandledRef.current) return;
        scanHandledRef.current = true;
        await applyQrTokenAndLogin(decodedText);
      };

      const tryStartDeviceId = async (deviceId) =>
        html5QrCode.start(deviceId, config, onScanSuccess, () => {});

      const tryStartConstraints = async (constraints) =>
        html5QrCode.start(constraints, config, onScanSuccess, () => {});

      const devices = await Html5Qrcode.getCameras();

      if (devices?.length) {
        const preferred = pickCameraDevice(devices);
        const order = [preferred, ...devices.filter((d) => d.id !== preferred.id)];
        let lastErr;
        let started = false;
        for (const dev of order) {
          try {
            await tryStartDeviceId(dev.id);
            started = true;
            break;
          } catch (e) {
            lastErr = e;
            try {
              await html5QrCode.stop();
            } catch (_) {
              /* ignore */
            }
          }
        }
        if (!started) {
          throw lastErr || new Error('Не удалось открыть ни одну камеру');
        }
        setQrScannerStarted(true);
        return;
      }

      try {
        await tryStartConstraints({ facingMode: 'environment' });
      } catch (e1) {
        await tryStartConstraints({ facingMode: 'user' });
      }
      setQrScannerStarted(true);
    } catch (e2) {
      await stopScanner();
      const msg = e2?.message || String(e2);
      const hint =
        msg.includes('NotFoundError') || msg.includes('not found')
          ? ' Используйте «Изображение с QR» или введите токен вручную.'
          : ' Разрешите доступ к камере или выберите изображение с QR.';
      setQrDialogError(`${msg}.${hint}`);
    } finally {
      setQrStarting(false);
    }
  };

  const handleCloseQr = () => {
    void stopScanner();
    setQrDialogError('');
    setQrScannerStarted(false);
    setQrOpen(false);
  };

  const handleQrFromFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setQrDialogError('');
    try {
      await stopScanner();
      const reader = new Html5Qrcode(QR_READER_ID);
      const text = await reader.scanFile(file, false);
      reader.clear();
      scanHandledRef.current = true;
      await applyQrTokenAndLogin(text);
    } catch {
      setQrDialogError('Не удалось распознать QR на изображении. Попробуйте другой файл или введите токен вручную.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    void unlockNotificationAudio();
    setError('');

    if (!deviceToken.trim()) {
      setError('Пожалуйста, введите токен устройства');
      return;
    }

    if (!deviceToken.includes('.')) {
      setError('Неверный формат токена. Токен должен быть в формате: <device_id>.<secret>');
      return;
    }

    setIsLoading(true);

    try {
      await login(deviceToken.trim());
      navigate('/orders');
    } catch (err) {
      setError(err.message || 'Ошибка входа. Проверьте токен устройства и попробуйте снова.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <Card sx={{ width: '100%', maxWidth: 450 }}>
          <CardContent sx={{ p: 4 }}>
            <Box display="flex" alignItems="center" justifyContent="center" mb={3}>
              <Coffee sx={{ fontSize: 40, mr: 1, color: 'primary.main' }} />
              <Typography variant="h4" component="h1">
                Quickly Coffee
              </Typography>
            </Box>

            <Typography variant="h6" align="center" gutterBottom>
              Вход для баристы
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Токен устройства"
                variant="outlined"
                value={deviceToken}
                onChange={(e) => setDeviceToken(e.target.value)}
                margin="normal"
                autoComplete="off"
                autoFocus
                disabled={isLoading}
                placeholder="1.ZwV2iVQ4pPpGqv0yC7WwXGk8R1s9Ld2b"
                helperText="Введите токен устройства в формате: <device_id>.<secret>"
              />

              <Box
                sx={{
                  mt: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  width: '100%',
                }}
              >
                <Button fullWidth type="submit" variant="contained" size="large" disabled={isLoading}>
                  {isLoading ? 'Вход...' : 'Войти'}
                </Button>
                <Button
                  type="button"
                  variant="outlined"
                  fullWidth
                  size="large"
                  startIcon={<QrCodeScanner />}
                  disabled={isLoading}
                  onClick={() => {
                    setQrDialogError('');
                    setQrScannerStarted(false);
                    setQrOpen(true);
                  }}
                >
                  Сканировать QR
                </Button>
              </Box>
            </form>

            <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2 }}>
              Токен устройства можно получить у администратора кофейни
            </Typography>
          </CardContent>
        </Card>
      </Box>

      <Dialog open={qrOpen} onClose={handleCloseQr} fullWidth maxWidth="sm">
        <DialogTitle>Сканирование QR</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Нажмите кнопку ниже — браузер запросит доступ к камере (это сработает только от вашего нажатия,
            не в фоне). Затем наведите камеру на QR с токеном устройства.
          </Typography>
          {qrDialogError && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {qrDialogError}
            </Alert>
          )}
          {!qrScannerStarted && (
            <Button
              fullWidth
              variant="contained"
              size="large"
              sx={{ mb: 2 }}
              disabled={qrStarting}
              onClick={() => void handleStartQrCamera()}
            >
              {qrStarting ? 'Запрос доступа…' : 'Включить камеру и сканировать'}
            </Button>
          )}
          <Box
            id={QR_READER_ID}
            sx={{
              width: '100%',
              minHeight: qrScannerStarted ? 260 : 80,
              bgcolor: qrScannerStarted ? 'transparent' : 'action.hover',
              borderRadius: 1,
              '& video': { borderRadius: 1, width: '100%', maxHeight: 320 },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 3, pb: 2 }}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={handleQrFromFile}
          />
          <Button variant="outlined" onClick={() => fileInputRef.current?.click()}>
            Изображение с QR
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button onClick={handleCloseQr}>Отмена</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default LoginForm;
